import { useFlag } from '@calendly/feature-check-react'
import type { Moment } from 'moment-timezone'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { rootApi as rootApiRest } from 'root-api'
import { rootApi as rootApiGraphql } from 'root-api-graphql'
import { timezoneSelector } from 'selectors/current_user'
import { useCurrentTime } from 'shared/contexts'

// Only consider cached GraphQL queries from our meetings APIs.
// This prevents accidentally treating other MFEs' GraphQL responses (e.g., CRM GetContacts)
// as "meetings" and overwriting the active meeting with a partial shape.
const MEETINGS_GRAPHQL_ENDPOINT_NAMES = new Set([
  // Old meetings GraphQL API (shared/api/meetings/graphql_api.ts)
  'getMeetings',
  'getMeeting',
  'searchMeetings',
  'getMeetingsFilterCount',
  'getSearchResultsCount',

  // Supergraph cmd list (`listMeetings` / `meetingsPageList`) — same `meetings.edges` cache shape.
  'listMeetingsSupergraph',
  'meetingsPageListSupergraph',
  'getMeetingSupergraph',
  'getMeetingByEitherIdSupergraph',

  // Recaps GraphQL API (features/recaps/graphql_api.js) – also returns meetings shapes
  'searchMeetingRecaps',
  'getMeetingRecaps',
])

/**
 * Hook to get a single meeting from RTK Query cache by UUID.
 * This ensures that when meeting data is updated in the cache (via polling, mutations, etc.),
 * the component automatically receives the updated data.
 *
 * Searches through ALL cached query results from both GraphQL and REST APIs:
 * - Realtime meetings GraphQL queries (getMeetingsRealtimeFuture, getMeetingsRealtimePast, getMeetingsRealtimeToday)
 * - Old meetings GraphQL queries (getMeetings, getMeeting, searchMeetings)
 * - Old meetings REST queries (getMeetings, getMeeting)
 *
 * This ensures meetings from the initial list load can be found even if parameters differ.
 *
 * @param meetingUuid - The UUID of the meeting to find
 * @param mapNodeToMeeting - Function to transform a GraphQL node to meeting format (feature-specific).
 *   Signature: (node, publisherTZ, hasMsTeamsFlag, currentTime) to match features/meetings/utils.js.
 * @param params - Optional context (e.g. `{ scopeType, scopeId }`) for call-site clarity; not used for cache lookup (UUID match only).
 * @returns The meeting data from cache, or null if not found
 */
export const useMeetingFromCache = (
  meetingUuid: string | null | undefined,
  mapNodeToMeeting: (node: any, publisherTZ: string, hasMsTeamsFlag?: boolean, currentTime?: Moment) => any,
  params?: any // Optional call-site context; not used for lookup (see JSDoc above)
) => {
  const timezone = useSelector(timezoneSelector)
  const currentTime = useCurrentTime()
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')

  // Access both RTK Query cache states (GraphQL and REST) to search across ALL cached queries
  // This allows us to find meetings from the initial list load even if params differ
  // Note: realtime meetings queries use rootApiGraphql (graphqlApi reducer path)
  const graphqlCacheState = useSelector((state: any) => state[rootApiGraphql.reducerPath]?.queries || {})
  const restCacheState = useSelector((state: any) => state[rootApiRest.reducerPath]?.queries || {})

  // Find meeting in cache by searching through all cached query results
  const meetingNode = useMemo(() => {
    if (!meetingUuid || !timezone) return null

    // Helper function to search through a cache state
    const searchCache = (cacheState: any, isGraphQL: boolean) => {
      const queryKeys = Object.keys(cacheState)

      for (const queryKey of queryKeys) {
        if (isGraphQL) {
          const querySubState = cacheState[queryKey]

          // Filter to meetings-related endpoints only.
          // This is critical because other MFEs may share the same reducerPath ('graphqlApi')
          // and store GraphQL query results that happen to contain a `meetings.edges` shape.
          // STRICT: Only process entries with a known meetings endpoint name.
          // If endpointName is missing or unknown, skip this entry entirely to prevent
          // cache pollution from CRM contacts or other non-meetings GraphQL queries.
          const endpointName: string | undefined = querySubState?.endpointName
          const isMeetingsEndpoint = endpointName && MEETINGS_GRAPHQL_ENDPOINT_NAMES.has(endpointName)
          if (isMeetingsEndpoint) {
            const queryData = querySubState?.data

            // Handle single meeting query (getMeeting) first - transformResponse returns the node directly.
            // queryData is just { uuid, ... } with no meetings.edges or meetings_search.edges,
            // so it must be checked outside the list-structure block.
            if (queryData && typeof queryData === 'object' && queryData.uuid === meetingUuid) {
              return queryData
            }

            // Check if this is a list-style meetings query by data structure
            const hasMeetingsStructure = queryData?.meetings?.edges || queryData?.meetings_search?.edges

            if (hasMeetingsStructure) {
              // Handle GraphQL response format with edges
              if (queryData?.meetings?.edges) {
                const matchingEdge = queryData.meetings.edges.find(
                  (edge: any) => edge?.node?.uuid === meetingUuid
                )
                if (matchingEdge?.node) {
                  return matchingEdge.node
                }
              }
              // Handle searchMeetings response format (meetings_search)
              if (queryData?.meetings_search?.edges) {
                const matchingEdge = queryData.meetings_search.edges.find(
                  (edge: any) => edge?.node?.uuid === meetingUuid
                )
                if (matchingEdge?.node) {
                  return matchingEdge.node
                }
              }
            }
          }
        } else {
          // REST API cache - derive from endpointName to avoid silent failures from queryKey format changes
          const querySubState = cacheState[queryKey]
          const endpointName = querySubState?.endpointName
          const isMeetingsRestQuery = endpointName === 'getMeetings' || endpointName === 'getMeeting'

          if (isMeetingsRestQuery) {
            const queryData = querySubState?.data
            // Single meeting query
            if (queryData?.uuid === meetingUuid || queryData?.id === meetingUuid) {
              return {
                ...queryData,
                // Map REST API fields to GraphQL node format
                start_time: queryData.startTime || queryData.start_time,
                end_time: queryData.endTime || queryData.end_time,
              }
            }
            // List query with results array (grouped by day)
            if (queryData?.results) {
              for (const day of queryData.results) {
                if (day?.events) {
                  const matchingMeeting = day.events.find(
                    (meeting: any) => meeting?.uuid === meetingUuid || meeting?.id === meetingUuid
                  )
                  if (matchingMeeting) {
                    return {
                      ...matchingMeeting,
                      start_time: matchingMeeting.startTime || matchingMeeting.start_time,
                      end_time: matchingMeeting.endTime || matchingMeeting.end_time,
                    }
                  }
                }
              }
            }
          }
        }
      }
      return null
    }

    // Search GraphQL cache first (most common case)
    const graphqlResult = searchCache(graphqlCacheState, true)
    if (graphqlResult) return graphqlResult

    // Fallback to REST cache
    const restResult = searchCache(restCacheState, false)
    if (restResult) return restResult

    return null
  }, [meetingUuid, graphqlCacheState, restCacheState, timezone])

  // Transform the node to meeting format (hasMsTeamsFlag + currentTime so Teams eligibility and past/future use correct values)
  const meeting = useMemo(() => {
    if (!meetingNode || !timezone) return null
    return mapNodeToMeeting(meetingNode, timezone, hasMsTeamsFlag, currentTime)
  }, [meetingNode, timezone, hasMsTeamsFlag, currentTime, mapNodeToMeeting])

  return meeting
}
